import React, { ChangeEvent, FC, ReactElement, useState } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { StandardTextFieldProps } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { clsx } from 'clsx';
import moment from 'moment';
import PhoneInput from 'react-phone-input-2';
import fr from 'react-phone-input-2/lang/fr.json';
// Components
import SvgIcon from '../icons/SvgIcon';
import PopperTimePicker, { FilterMode } from '../timePicker/popperTimePicker';
// Resources
import { stringsRes } from '../../resources/strings';
import { addLeadingZero, capitalizeFirstLetter } from '../../utils/strings';
import { IconCommon } from '../../resources/images';
import useGlobalStyles from '../hook/useGlobalStyles';
import { getUser } from '../../localStorageAPI';
// Phone
import 'react-phone-input-2/lib/material.css';

export interface InputFieldProps extends StandardTextFieldProps {
    mode?: 'text' | 'price' | 'password' | 'tel' | 'none';
    errorText?: string;
    isValidFn?: (value, countryData) => boolean;
    test?: { regex: RegExp; label: string };
    readOnly?: boolean;
    onlyNumber?: boolean;
    allowDigit?: boolean;
    hourMinFormat?: boolean;
    hourMinPicker?: boolean;
    filterMode?: FilterMode;
    startAdornment?: ReactElement;
}

const InputField: FC<InputFieldProps> = (props): ReactElement => {
    const {
        value,
        onChange,
        onSubmit,
        isValidFn,
        name,
        mode = 'text',
        errorText,
        test,
        label,
        placeholder,
        readOnly,
        onlyNumber,
        allowDigit,
        hourMinFormat,
        hourMinPicker,
        filterMode = FilterMode.AFTER_DATE,
        startAdornment,
        className,
        ...rest
    } = props;
    const classes = useStyles();
    const globalClasses = useGlobalStyles();

    const user = getUser();

    // State
    const [testError, setTestError] = useState<string>('');
    const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
    const [isFocused, setFocused] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const oldValueRef = React.useRef(`${value}`);

    const renderStartAdornment = () => {
        return mode === 'price' && value ? (
            <>
                <span style={{ color: '#74767D', position: 'relative', top: 9 }}>€</span>
                {startAdornment}
            </>
        ) : (
            startAdornment
        );
    };

    const renderEndAdornment = () => {
        if (rest.disabled || readOnly) {
            return null;
        }
        if (value && (mode === 'text' || mode === 'price' || mode === 'tel')) {
            return (
                <IconButton
                    disableRipple
                    tabIndex={-1}
                    className={classes.endIcon}
                    aria-label="clear text"
                    onClick={() =>
                        onChange({
                            target: { value: '', name, id: name },
                        } as unknown as ChangeEvent<HTMLInputElement>)
                    }
                >
                    <SvgIcon icon={IconCommon.CircleClose} color="blue" fill="none" size="small" />
                </IconButton>
            );
        }
        if (value && mode === 'password') {
            return (
                <IconButton
                    disableRipple
                    tabIndex={-1}
                    className={classes.endIcon}
                    aria-label="toggle password visibility"
                    onClick={() => setPasswordVisible(!passwordVisible)}
                >
                    <SvgIcon
                        icon={passwordVisible ? IconCommon.Eye : IconCommon.EyeOff}
                        stroke="#74767D"
                        size="small"
                    />
                </IconButton>
            );
        }
        return null;
    };

    const onFocus = () => {
        setFocused(true);
    };

    const onBlur = (/* e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement> */) => {
        setFocused(false);
        if (value && test?.regex && !test.regex.test(value as string)) {
            setTestError(test.label);
        }
    };

    const customOnChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (testError) {
            setTestError('');
        }
        if (onlyNumber) {
            let regex = /^[0-9\b]+$/;
            if (allowDigit) {
                regex = /^[0-9]+([.][0-9]*)?$/;
            }

            if (e.target.value === '' || regex.test(e.target.value)) {
                onChange(e);
            }
        } else if (hourMinFormat) {
            const regex = /^[0-9:\b]+$/;

            if (e.target.value === '' || regex.test(e.target.value)) {
                const oldValue = oldValueRef.current;
                const newValue = e.target.value;
                const isRemoving = oldValue?.length > newValue?.length;

                // console.log(oldValue, newValue);
                let res = e.target.value.slice(0, 5);
                if (!res.includes(':') && res.length === 2 && isRemoving) {
                    res = e.target.value.slice(0, 1);
                }
                if (res.includes(':') && isRemoving) {
                    const indexChange = newValue.indexOf(':');
                    if (indexChange < 2) {
                        const tmp = newValue.replace(':', '');
                        res = `${tmp.substring(0, indexChange + 1)}:${tmp.substring(indexChange + 1)}`;
                    }
                }

                if (!res.includes(':') && res.length > 1) {
                    e.target.value = `${addLeadingZero(Math.min(parseInt(res.slice(0, 2), 10), 23))}:${
                        res.slice(2, 4) ? Math.min(parseInt(res.slice(2, 4), 10), 59) : ''
                    }`;
                } else if (res.includes(':') && res.length > 4) {
                    e.target.value = `${addLeadingZero(Math.min(parseInt(res.slice(0, 2), 10), 23))}:${
                        res.slice(3, 5) ? addLeadingZero(Math.min(parseInt(res.slice(3, 5), 10), 59)) : ''
                    }`;
                } else if (res.includes(':') && res.length > 3) {
                    e.target.value = `${addLeadingZero(Math.min(parseInt(res.slice(0, 2), 10), 23))}:${
                        res.slice(3, 5) ? Math.min(parseInt(res.slice(3, 5), 10), 59) : ''
                    }`;
                } else {
                    e.target.value = res;
                }
                // after everything
                oldValueRef.current = e.target.value;
                onChange(e);
            }
        } else {
            onChange(e);
        }
    };

    if (mode === 'tel') {
        return (
            <div>
                <label htmlFor={rest?.id} className={globalClasses.visuallyHidden}>
                    {placeholder}
                </label>

                <PhoneInput
                    inputClass={classes.inputTel}
                    inputStyle={{
                        ...(!errorText ? { border: '1.5px solid transparent' } : { boxShadow: 'unset' }),
                    }}
                    inputProps={{ id: rest?.id, onFocus: rest?.onFocus }}
                    containerClass={classes.containerTel}
                    buttonClass={classes.buttonTel}
                    isValid={!errorText && isValidFn}
                    enableSearch
                    searchPlaceholder={stringsRes.common.search}
                    searchNotFound={stringsRes.common.noResult}
                    searchClass={classes.searchTel}
                    dropdownClass={classes.dropdownTel}
                    specialLabel=""
                    placeholder={`${placeholder}${rest.required ? ' *' : ''}`}
                    country="fr"
                    localization={user?.language === 'fr' ? fr : undefined}
                    disabled={rest.disabled}
                    value={`${value}`}
                    onEnterKeyPress={onSubmit}
                    onChange={(phone, data, e) => {
                        return customOnChange({
                            target: {
                                id: name,
                                name,
                                value: phone.startsWith('0') ? phone.replace('0', (data as any)?.dialCode) : phone,
                                data,
                                e,
                            },
                        } as unknown as ChangeEvent<HTMLInputElement>);
                    }}
                    onBlur={rest?.onBlur}
                />
                {value && (
                    <div className={classes.buttonRelative}>
                        <div className={classes.buttonAbsolute}>{renderEndAdornment()}</div>
                    </div>
                )}
                {errorText && (
                    <p className={`MuiFormHelperText-root MuiFormHelperText-contained Mui-error ${classes.errorTel}`}>
                        {errorText}
                    </p>
                )}
            </div>
        );
    }

    return (
        <>
            <TextField
                // Only the time picker popper needs the anchor: keeping the ref callback on every field
                // makes MUI re-run it on each render (fork-ref), which loops on a `select` TextField.
                inputRef={hourMinPicker ? (ref) => setAnchorEl(ref) : undefined}
                id={name?.split(' ')?.[0]?.toLowerCase() ?? name}
                name={name}
                label={label ?? capitalizeFirstLetter(placeholder ?? name)}
                placeholder={placeholder}
                className={clsx(classes.input, readOnly ? classes.readOnly : null, className)}
                value={value}
                disabled={readOnly}
                onChange={customOnChange}
                onFocus={onFocus}
                onBlur={onBlur}
                onKeyUp={(e) => (onSubmit && e.code === 'Enter' ? onSubmit(e) : null)}
                type={passwordVisible ? 'text' : mode}
                inputMode={onlyNumber ? 'numeric' : 'text'}
                variant="filled"
                InputProps={{
                    startAdornment: renderStartAdornment(),
                    endAdornment: renderEndAdornment(),
                    classes: { input: classes.autofill },
                    disableUnderline: true,
                    style: {
                        // Remove arrow for number input
                        appearance: 'none',
                        WebkitAppearance: 'none', // Chrome, Safari, Edge
                        MozAppearance: 'textfield', // Pour Firefox
                    },
                }}
                error={!!errorText || !!testError}
                helperText={errorText || testError}
                autoComplete={hourMinPicker ? 'off' : 'on'}
                {...rest}
            />
            {hourMinPicker && isFocused && (
                <PopperTimePicker
                    anchorEl={anchorEl}
                    empty={false}
                    timeValue={moment(
                        new Date().setHours(
                            +(value as unknown as string).split(':')[0],
                            +(value as unknown as string).split(':')[1],
                        ),
                    )}
                    onChangeTime={(date, dateString) => {
                        customOnChange({
                            target: { id: name, name, value: dateString },
                        } as unknown as ChangeEvent<HTMLInputElement>);
                    }}
                    closeModal={() => {}}
                    validDate={new Date()}
                    filterMode={filterMode}
                />
            )}
        </>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        autofill: {
            /* Remove autofill color */
            '&:-webkit-autofill': {
                WebkitBackgroundClip: `text`,
                WebkitTextFillColor: theme.palette.primary.main,
            },
        },
        readOnly: {
            '& .Mui-disabled': {
                color: theme.palette.colors.text.label,
            },
            '& input.Mui-disabled': {
                '-webkit-text-fill-color': 'unset',
                color: theme.palette.primary.main,
            },
        },
        input: {
            '& .MuiFilledInput-root': {
                background: theme.palette.colors.background.primaryElement,
                borderRadius: 6,
                fontWeight: 500,
                fontSize: 16,
                letterSpacing: '0.2px',
                border: `1.5px solid transparent`,
            },
            '& div.Mui-focused': {
                background: theme.palette.colors.background.secondaryBtn,
                border: `1.5px solid ${theme.palette.secondary.main}`,
            },
            '& .MuiFilledInput-underline:before': {
                borderBottomColor: 'transparent',
            },
            '& .MuiFilledInput-underline:after': {
                borderBottomColor: 'transparent',
            },
            '& label.Mui-focused': {
                color: theme.palette.secondary.main,
                fontWeight: 500,
                fontSize: 16,
                letterSpacing: '0.2px',
            },
            '& label': {
                fontWeight: 500,
                fontSize: 16,
                letterSpacing: '0.2px',
                marginLeft: theme.spacing(0.5),
            },
            '& label.Mui-focused.Mui-error': {
                color: theme.palette.colors.text.error,
                fontWeight: 500,
                fontSize: 16,
                letterSpacing: '0.2px',
            },
            '& label.MuiFormLabel-root.Mui-error': {
                color: theme.palette.colors.text.error,
            },
            '& :focus': {
                borderRadius: 6,
            },
            '& .MuiFormHelperText-root.Mui-error': {
                color: theme.palette.colors.text.error,
            },
            '& div.Mui-error': {
                border: `1.5px solid ${theme.palette.colors.text.error}`,
            },
            '& .MuiFilledInput-adornedEnd': {
                paddingRight: 0,
            },
            '& .MuiFilledInput-input': {
                paddingLeft: theme.spacing(2),
                paddingRight: theme.spacing(2),
            },
            '& .MuiInputBase-inputMultiline': {
                paddingLeft: theme.spacing(0.25),
                paddingRight: theme.spacing(0.25),
            },
            borderRadius: 6,
            display: 'flex',
            flex: 1,
            marginBottom: theme.spacing(1),
            caretColor: theme.palette.secondary.main,
        },
        endIcon: {
            padding: '20px 16px',
            '&.MuiIconButton-root:hover': {
                backgroundColor: 'transparent',
            },
        },
        errorTel: {
            color: `${theme.palette.colors.text.error} !important`,
            margin: 0,
            marginLeft: 14,
            marginRight: 14,
            fontSize: '0.8571428571428571rem',
            marginTop: 3,
            textAlign: 'left',
            fontFamily: 'Roboto',
            fontWeight: 400,
            lineHeight: '1.66',
        },
        searchTel: {
            display: 'flex',
            '& .search-box': {
                display: 'flex',
                flex: 1,
                marginRight: 6,
            },
        },
        dropdownTel: {
            '&.country-list': {
                textAlign: 'left',
            },
        },
        inputTel: {
            width: '100% !important',
            color: theme.palette.primary.main,
            background: `${theme.palette.colors.background.primaryElement} !important`,
            fontSize: `16px !important`,
            fontWeight: 500,
            fontFamily: 'Roboto',
            '&:-webkit-autofill': {
                WebkitBackgroundClip: `text`,
                WebkitTextFillColor: theme.palette.primary.main,
                transition: 'background-color 5000s ease-in-out 0s',
            },
            '&.form-control:focus': {
                border: `1.5px solid ${theme.palette.secondary.main} !important`,
                boxShadow: 'unset !important',
            },
            '&.form-control.invalid-number': {
                border: `1.5px solid ${theme.palette.colors.text.error} !important`,
            },
        },
        containerTel: {
            display: 'flex',
            borderRadius: 6,
            caretColor: theme.palette.secondary.main,
        },
        buttonTel: {
            border: '1.5px solid transparent',
            marginLeft: 6,
        },
        buttonRelative: {
            top: -58,
            position: 'relative',
        },
        buttonAbsolute: {
            position: 'absolute',
            top: 0,
            right: 12,
        },
    }),
);

export default InputField;
